import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  SearchField,
  SelectField,
  Tooltip,
  Typography,
} from "@tiny/components";
import Refresh from "@tiny/icons/generated/Refresh";
import TrashFull from "@tiny/icons/generated/TrashFull";
import Merge from "@tiny/icons/generated/Merge";
import AutoAwesome from "@tiny/icons/generated/AutoAwesome";
import { useAudienceFilter } from "./AudienceFilter.hooks";
import { useStyles } from "./AudienceFilter.styles";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { FETCH_AND_SEND_TRAFFIC_SHAPING } from "../../../constants";
import RetentionActivationDialog from "../../RetentionActivationDialog";
import ManageRetentionDialog from "../../ManageRetentionDialog";
import { useTinyHost } from "@tiny/host-contract";
import React from "react";

const RETENTION_PRODUCT_IDS = {
  STAGE: "c1ef6fb3-cc76-4ccd-9307-b5861d1444f9",
  PROD: "e3f9778c-48dd-4114-b7cf-7c9679703897",
};

function AudienceFilter() {
  const retentionProductId = useTinyHost().capabilities.isStagingEnvironment
    ? RETENTION_PRODUCT_IDS.STAGE
    : RETENTION_PRODUCT_IDS.PROD;
  const audienceFilterTour = useTinyHost().slots?.audienceFilterTour;
  const classes = useStyles();
  const { mdUp } = useBreakpoints();
  const {
    areAllSelectedOnCurrentPage,
    checkAllDisabled,
    enableBulkDelete,
    enableSelectionActions,
    hasSelected,
    nrOfSelectedAudiences,
    onClickMergeAudiences,
    onChangeSort,
    onDeleteAudiences,
    reloadAudiences,
    searchKeyword,
    selectedText,
    selectionDisabled,
    setSearchKeyword,
    sortOptions,
    sortedBy,
    toggleAllOnCurrentPage,
    unselectAll,
    deleteDisabled,
    mergeDisabled,
    handleOpenAudienceSummary,
    onClickTrafficControl,
    handleOpenSegmentPerformance,
    isRetentionActive,
    retentionDialogOpen,
    openRetentionDialog,
    closeRetentionDialog,
    handleRetentionSuccess,
    handleManageRetention,
    manageRetentionDialogOpen,
    closeManageRetentionDialog,
  } = useAudienceFilter();

  const { capabilities } = useTinyHost();
  const campaignWorkflowType = capabilities.campaignWorkflowType;

  const handleTourStepAfter = React.useCallback(
    (index: number) => {
      if (index === 1) openRetentionDialog();
    },
    [openRetentionDialog]
  );

  const audienceSummaryDisabled = !hasSelected;
  const newEngagerDisabled = nrOfSelectedAudiences !== 1;
  const mergeButtonDisabled = nrOfSelectedAudiences < 2 || mergeDisabled();

  return (
    <div className={classes.root}>
      {/* Row 1: search + sort */}
      <div className={classes.top}>
        {mdUp && (
          <div className={classes.check}>
            <Checkbox
              color="primary"
              checked={areAllSelectedOnCurrentPage}
              onChangeValue={toggleAllOnCurrentPage}
              disabled={checkAllDisabled}
            />
          </div>
        )}
        {hasSelected && enableSelectionActions ? (
          <div className={classes.selected}>
            <Button
              onClick={unselectAll}
              variant="text"
              noPadding
              disabled={selectionDisabled}
            >
              Deselect all
            </Button>
            <Typography color="grey" tone={600} ml={5} variant="body2">
              {selectedText}
            </Typography>
          </div>
        ) : (
          <div className={classes.search}>
            <SearchField
              value={searchKeyword}
              placeholder="Search"
              onChangeValue={setSearchKeyword}
              className={classes.searchField}
            />
          </div>
        )}
        <div className={classes.sort}>
          {mdUp && (
            <IconButton onClick={reloadAudiences} size="large">
              <Refresh />
            </IconButton>
          )}
          <SelectField
            options={sortOptions}
            value={sortedBy}
            onChange={onChangeSort}
            aria-label="Sort"
            fullWidth={!mdUp}
          />
        </div>
      </div>
      {/* Divider + actions row */}
      {enableSelectionActions && (mdUp || hasSelected) && (
        <>
          <Divider className={classes.divider} />
          <div data-tour="audience-actions-bar" className={classes.actions}>
            {campaignWorkflowType === FETCH_AND_SEND_TRAFFIC_SHAPING &&
              capabilities.audienceSummaryVisible !== false && (
                <Tooltip
                  arrow
                  title={
                    audienceSummaryDisabled
                      ? "Select one or more audiences to view an audience summary"
                      : "View a summary of the selected audiences"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleOpenAudienceSummary}
                      className={classes.audienceSummaryButton}
                      disabled={audienceSummaryDisabled}
                    >
                      Audience Summary
                    </Button>
                  </span>
                </Tooltip>
              )}
            {capabilities.newEngagerPerformanceVisible !== false && (
              <Tooltip
                arrow
                title={
                  newEngagerDisabled
                    ? "Select exactly one audience to check new engager performance"
                    : "View new engager performance for the selected audience"
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenSegmentPerformance}
                    className={classes.audienceSummaryButton}
                    disabled={newEngagerDisabled}
                  >
                    New Engager Performance
                  </Button>
                </span>
              </Tooltip>
            )}
            {capabilities.retentionVisible !== false &&
              (isRetentionActive ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleManageRetention}
                  className={classes.audienceSummaryButton}
                >
                  Manage Retention.com
                </Button>
              ) : (
                <Button
                  data-tour="retention-button"
                  variant="outlined"
                  color="primary"
                  onClick={openRetentionDialog}
                  className={classes.audienceSummaryButton}
                >
                  Get Leads via Retention.com
                </Button>
              ))}
            <Tooltip
              arrow
              title={
                nrOfSelectedAudiences < 2
                  ? "Select 2 or more audiences to merge them"
                  : mergeDisabled()
                  ? "All selected audiences must be in Completed status to merge"
                  : "Merge the selected audiences into one"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Merge />}
                  onClick={onClickMergeAudiences}
                  className={classes.mergeButton}
                  disabled={mergeButtonDisabled}
                >
                  Merge
                </Button>
              </span>
            </Tooltip>
            {capabilities.aiTrafficControlEnabled && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AutoAwesome />}
                onClick={onClickTrafficControl}
                className={classes.mergeButton}
                disabled={selectionDisabled}
              >
                Activate AI Traffic Control
              </Button>
            )}
            {enableBulkDelete && hasSelected && (
              <Tooltip
                arrow
                title={
                  deleteDisabled()
                    ? "Cannot delete selected audience(s). They either have been assigned to a form or a campaign or have a customer from integrations."
                    : "Delete the selected audiences"
                }
                placement="top-start"
              >
                <span>
                  <Button
                    variant="outlined"
                    color="red"
                    endIcon={<TrashFull />}
                    onClick={onDeleteAudiences}
                    className={classes.deleteButton}
                    disabled={deleteDisabled()}
                  >
                    Delete
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </>
      )}
      <RetentionActivationDialog
        open={retentionDialogOpen}
        onClose={closeRetentionDialog}
        productId={retentionProductId}
        onSuccess={handleRetentionSuccess}
      />
      <ManageRetentionDialog
        open={manageRetentionDialogOpen}
        onClose={closeManageRetentionDialog}
        productId={retentionProductId}
      />
      {audienceFilterTour?.({
        enabled: !isRetentionActive,
        onStepAfter: handleTourStepAfter,
      })}
    </div>
  );
}

export default AudienceFilter;
